import React from 'react'

import { SERVICE_PAGE_PILLAR_ICONS } from '../../constants/serviceHeroAssets'

/**
 * Hero for individual service / solution pages (two-column: copy + existing PNG illustration).
 *
 * @param {Object} props
 * @param {string} props.label - Eyebrow, e.g. "Services" (uppercased in CSS)
 * @param {string} props.title - Main heading (plain text; line breaks via `\n` if needed)
 * @param {string} props.description - Supporting paragraph
 * @param {string} props.heroImage - Bundled image URL (imported asset)
 * @param {string} props.imageAlt - Alt text for the illustration
 * @param {number} props.imageWidth - Intrinsic width of `heroImage` (CLS)
 * @param {number} props.imageHeight - Intrinsic height of `heroImage` (CLS)
 * @param {string} [props.sectionId] - Optional `id` on the section
 * @param {string} [props.pillarIcon] - Optional small pillar graphic (defaults to app pillar SVG)
 * @param {number} [props.pillarIconWidth] - Display width in px for the pillar icon
 *
 * Example:
 * `import { SERVICE_HERO_ASSETS } from '../../constants/serviceHeroAssets'`
 * then `heroImage={SERVICE_HERO_ASSETS.app.src}` and matching `imageWidth` / `imageHeight`.
 */
const ServicePageHero = ({
  label,
  title,
  description,
  heroImage,
  imageAlt,
  imageWidth,
  imageHeight,
  sectionId,
  pillarIcon = SERVICE_PAGE_PILLAR_ICONS.app.src,
  pillarIconWidth = SERVICE_PAGE_PILLAR_ICONS.app.width,
}) => {
  const headingId = sectionId ? `${sectionId}-heading` : 'service-page-hero-heading'
  return (
    <section
      className="service-page-hero"
      id={sectionId || undefined}
      aria-labelledby={headingId}
    >
      <div className="content container">
        <div className="row">
          <div className="col-6 col-12-medium service-page-hero__text">
            <p className="service-page-hero__label">{label}</p>
            <h1 className="service-page-hero__title" id={headingId}>
              {title}
            </h1>
            <div className="service-page-hero__icon-row" aria-hidden="true">
              <span className="service-page-hero__pillar-icon">
                <img
                  src={pillarIcon}
                  alt=""
                  style={{ width: pillarIconWidth, height: 'auto' }}
                  loading="eager"
                  decoding="async"
                  fetchPriority="low"
                />
              </span>
            </div>
            <p className="service-page-hero__description">{description}</p>
          </div>
          <div className="col-6 col-12-medium service-page-hero__media">
            <img
              className="service-page-hero__img"
              src={heroImage}
              alt={imageAlt}
              width={imageWidth}
              height={imageHeight}
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </section>
  )
}

export default ServicePageHero
